import type {
  ActivityEntry,
  AgentAttachment,
  AgentMessage,
  AgentTrigger,
  AgentTurn,
  AgentTurnStatus,
  AgentWithContext,
  AgentPairingCode,
  McpServer,
  McpServerKind,
  RemoteMachine,
  RemoteMachinesView,
  AgentFolder,
  SavedLayoutNav,
  SavedLayoutNode,
  WorkspaceLayout,
  AuthState,
  Invite,
  InvitableRole,
  BoardIntakeSummary,
  IntakeMessageWithFiles,
  IntakeRejection,
  BoardColumn,
  BoardDetail,
  BoardKind,
  Canvas,
  CanvasDoc,
  CanvasEdge,
  CanvasEdgeKind,
  CanvasNode,
  ColumnKind,
  DurationKind,
  Mention,
  NotificationPrefs,
  PushDevice,
  MentionStatus,
  MentionWithContext,
  Priority,
  Project,
  ProjectBrowse,
  ProjectDirectory,
  ProjectOverview,
  ProjectEnvStatus,
  ProjectUsage,
  WriteProjectEnvResult,
  ResponseStage,
  ResponseStatus,
  ResponseTurnWithContext,
  ResponseWithContext,
  TaskResponse,
  TaskResponseSummary,
  Task,
  TaskComment,
  TaskDetail,
  TaskWithContext,
  StatusDashboard,
  AppsStatus,
  ClaudeAccountsView,
  EngineSettings,
  EngineType,
  User,
  BoardWhatsAppSummary,
  WhatsAppChat,
  WhatsAppImport,
  TaskPhoto,
  VoiceEngines,
  WaitingCard,
  WaitingChatQuestion,
  WaitingReviewCard,
  Preview,
} from "./types";

/** Error carrying the server's machine-readable code so callers can branch on it. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Fired when the server says the session is gone (expired, signed out elsewhere,
 * or the account was removed), so the app can drop back to the sign-in screen
 * instead of showing a page of errors.
 */
export const AUTH_EXPIRED_EVENT = "board:auth-expired";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // The session cookie rides along on its own (same origin). Who made a change is
  // decided by the server from that cookie, never by anything this client sends.
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...init?.headers,
    },
  });

  if (response.status === 401 && !path.startsWith("/auth/")) window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  if (response.status === 204) return undefined as T;
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as { error?: { code: string; message: string; details?: Record<string, unknown> } })?.error;
    throw new ApiError(
      response.status,
      error?.code ?? "unknown",
      error?.message ?? `Request failed with ${response.status}`,
      error?.details,
    );
  }
  return payload as T;
}

const body = (data: unknown) => ({ body: JSON.stringify(data) });

/** A file streamed to an agent ahead of its message. Mirrors core's `AgentUpload`. */
export interface AgentUpload {
  uploadId: string;
  filename: string;
  mime: string;
  bytes: number;
}

/**
 * One file for an agent's chat, sent as the raw body (no base64) so a 100MB APK is
 * no harder than a screenshot. XHR rather than fetch, because only XHR reports
 * upload progress; `onProgress` gets the fraction sent. Abort with `signal`.
 */
function uploadAgentFile(agentId: string, file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal): Promise<AgentUpload> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/agents/${agentId}/uploads`);
    xhr.setRequestHeader("content-type", file.type || "application/octet-stream");
    xhr.setRequestHeader("x-filename", encodeURIComponent(file.name || "pasted"));
    xhr.responseType = "json";
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status === 401) window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
      if (xhr.status >= 200 && xhr.status < 300) return resolve(xhr.response as AgentUpload);
      const error = (xhr.response as { error?: { code: string; message: string; details?: Record<string, unknown> } } | null)?.error;
      reject(new ApiError(xhr.status, error?.code ?? "unknown", error?.message ?? `Upload of ${file.name} failed with ${xhr.status}`, error?.details));
    };
    xhr.onerror = () => reject(new ApiError(0, "network", `Upload of ${file.name} failed: the connection dropped`));
    xhr.onabort = () => reject(new ApiError(0, "aborted", `Upload of ${file.name} was cancelled`));
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

/** A token for the HTTP MCP endpoint, without its value. Mirrors core's `McpToken`. */
export interface McpToken {
  id: string;
  name: string;
  boardId: string | null;
  createdBy: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

/** How a project's Claude Code sessions reach one board. Mirrors core's `BoardMcpConfig`. */
export interface BoardMcpConfig {
  boardId: string;
  boardName: string;
  serverName: string;
  projectPath: string | null;
  command: string;
  mcpJson: { mcpServers: Record<string, { command: string; args: string[]; env: Record<string, string> }> };
}

export interface CreateBoardPayload {
  name: string;
  durationKind: DurationKind;
  description?: string;
  anchor?: string;
  endsAt?: string;
  columns?: string[];
  /** Project id, slug, name or path. The default for every card on the board. */
  project?: string | null;
  /** Every project the board covers, in order; the default is always one of them. */
  projects?: string[];
  /** `canvas`: a graph of nodes with a root, no columns (leave `columns` out). */
  kind?: BoardKind;
}

/** What a canvas node takes on create; every field but the parent also on update. */
export interface CanvasNodePayload {
  parentNodeId?: string;
  kind?: "group" | "link" | "task";
  title?: string | null;
  taskId?: string;
  note?: string | null;
  icon?: string | null;
  imageUrl?: string | null;
  docs?: CanvasDoc[];
  color?: string | null;
  collapsed?: boolean;
  x?: number | null;
  y?: number | null;
}

export interface CreateTaskPayload {
  title: string;
  description?: string;
  column?: string;
  assignee?: string | null;
  priority?: Priority;
  dueAt?: string | null;
  /** Overrides the board's project. `null` clears the override, it does not unset one. */
  project?: string | null;
  /** Parts of the codebase the card touches; [] or null clears them. */
  areas?: string[] | null;
}

export interface ProjectPayload {
  name: string;
  /** Absolute path, or one starting with `~`. Checked server-side before it is stored. */
  path: string;
  description?: string | null;
  /** One line of shell run before a parallel run's branch is merged; null removes it. */
  mergeCheck?: string | null;
}

/** A server's name, or its name with the only tools of it the agent may use. */
export type McpAttachment = string | { server: string; tools: string[] };

export interface McpServerPayload {
  name?: string;
  kind?: McpServerKind;
  icon?: string | null;
  description?: string | null;
  url?: string;
  command?: string;
  args?: string[];
  /** name → value sets a key; null removes it. Never read back. */
  env?: Record<string, string | null>;
  headers?: Record<string, string | null>;
  tools?: string[];
}

export interface AgentPayload {
  name: string;
  handle?: string;
  /** A registered project's id. Required: an agent is a session in a directory. */
  project: string;
  instructions?: string | null;
  model?: string | null;
  /** Run on this while `model` is at its usage limit; null is no fallback. */
  fallbackModel?: string | null;
  allowedTools?: string | null;
  contextLimit?: number | null;
  /** Assigned cards that may run at once in their own worktrees (1–8). */
  maxParallel?: number | null;
  /** Avatar icon and tint keys (`lib/avatars.tsx`); left out, the server infers them. */
  icon?: string | null;
  iconColor?: string | null;
  /** The MCP servers it carries, replacing the set: a registered name, or one with the tools to allow. Admin only. */
  mcpServers?: McpAttachment[];
  /** Pinned under the master in the list; admin only, and the master cannot be unpinned. */
  pinned?: boolean;
  /** The list folder to group it in; null takes it out (a folder left empty is deleted). Admin only. */
  folderId?: string | null;
}

/** A new foreign agent (core's `CreateForeignAgentInput`). It has no project here. */
export interface ForeignAgentPayload {
  name: string;
  handle?: string;
  /** The one board its calls are confined to; null or absent is every board. */
  boardId?: string | null;
  instructions?: string | null;
  model?: string | null;
  allowedTools?: string | null;
  icon?: string | null;
  iconColor?: string | null;
}

/**
 * A voice request: the body is raw WAV bytes, so not `request`, which speaks JSON.
 * Errors come back as `ApiError` all the same, with the status (429 = the engine is busy).
 */
async function voiceRequest(path: string, init: RequestInit): Promise<Response> {
  const response = await fetch(`/api${path}`, init);
  if (response.status === 401) window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: { code: string; message: string } } | null;
    throw new ApiError(response.status, payload?.error?.code ?? "unknown", payload?.error?.message ?? `Request failed with ${response.status}`);
  }
  return response;
}

/** A machine as the form sends it; `privateKey` is write-only and optional on an edit. */
export interface RemoteMachinePayload {
  name: string;
  host: string;
  port?: number;
  user: string;
  privateKey?: string;
  notes?: string | null;
  /** Agent ids that may use it; null is every agent. */
  agents?: string[] | null;
}

export const api = {
  /** `publicKey` null: the server has no VAPID keys, so push is off. Admin only. */
  pushConfig: () => request<{ publicKey: string | null }>("/push/vapid-public-key"),
  pushDevices: () => request<{ devices: PushDevice[] }>("/push/subscriptions"),
  /** Saves this browser's subscription (idempotent: the endpoint is the identity). */
  savePushSubscription: (subscription: PushSubscriptionJSON & { label: string | null }) =>
    request<{ device: PushDevice }>("/push/subscriptions", { method: "POST", ...body(subscription) }),
  removePushDevice: (deviceId: string) => request<{ id: string }>(`/push/subscriptions/${encodeURIComponent(deviceId)}`, { method: "DELETE" }),
  testPush: () => request<{ devices: number; sent: number }>("/push/test", { method: "POST" }),
  notificationPrefs: () => request<{ prefs: NotificationPrefs }>("/me/notification-prefs"),
  setNotificationPrefs: (patch: Partial<NotificationPrefs>) =>
    request<{ prefs: NotificationPrefs }>("/me/notification-prefs", { method: "PUT", ...body(patch) }),

  /** Whether the server can transcribe dictation. Admin only, like the agent chats that use it. */
  voiceEngines: () => request<VoiceEngines>("/voice"),
  /** A 16 kHz mono WAV to text; an empty string means nothing was heard. */
  transcribe: async (wav: Blob, agentId?: string, signal?: AbortSignal) => {
    const query = agentId ? `?agentId=${encodeURIComponent(agentId)}` : "";
    const response = await voiceRequest(`/voice/stt${query}`, { method: "POST", headers: { "content-type": "audio/wav" }, body: wav, signal });
    return (await response.json()) as { text: string };
  },
  /** The detail fields come back only for an admin. */
  health: () => request<{ ok: boolean; revision: number; logFile?: string; database?: string }>("/health"),
  revision: () => request<{ revision: number }>("/meta/revision"),
  users: () => request<{ users: User[] }>("/users"),
  status: (tz: string) => request<StatusDashboard>(`/status?tz=${encodeURIComponent(tz)}`),
  apps: () => request<{ apps: (AppsStatus & { observedAt: string; stale: boolean }) | null }>("/apps"),
  claudeAccounts: () => request<{ accounts: ClaudeAccountsView | null }>("/claude-accounts"),
  setClaudeAccount: (name: string, force = false) =>
    request<{ accounts: ClaudeAccountsView }>("/claude-accounts/active", { method: "PUT", ...body(force ? { name, force } : { name }) }),
  engineSettings: () => request<{ settings: EngineSettings }>("/engine"),
  setEngineSettings: (payload: { activeEngine?: EngineType; agyModel?: string; claudeModel?: string }) =>
    request<{ settings: EngineSettings }>("/engine", { method: "PUT", ...body(payload) }),

  authState: () => request<AuthState>("/auth/state"),
  setup: (payload: { email: string; password: string; displayName?: string }) =>
    request<{ user: User }>("/auth/setup", { method: "POST", ...body(payload) }),
  login: (payload: { email: string; password: string }) =>
    request<{ user: User }>("/auth/login", { method: "POST", ...body(payload) }),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  changePassword: (payload: { current: string; next: string }) =>
    request<void>("/auth/me/password", { method: "PATCH", ...body(payload) }),
  previewInvite: (token: string) =>
    request<{ email: string; role: InvitableRole; invitedByName: string }>(`/auth/invites/token/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, payload: { displayName: string; password: string }) =>
    request<{ user: User }>(`/auth/invites/token/${encodeURIComponent(token)}/accept`, { method: "POST", ...body(payload) }),
  invites: () => request<{ invites: Invite[] }>("/auth/invites"),
  createInvite: (payload: { email: string; role: InvitableRole }) =>
    request<{ invite: Invite; token: string }>("/auth/invites", { method: "POST", ...body(payload) }),
  revokeInvite: (inviteId: string) => request<Invite>(`/auth/invites/${inviteId}`, { method: "DELETE" }),
  members: () => request<{ members: User[] }>("/auth/members"),
  mcpTokens: (boardId?: string) =>
    request<{ tokens: McpToken[] }>(`/mcp-tokens${boardId ? `?boardId=${encodeURIComponent(boardId)}` : ""}`),
  /** The token value is in this response only. Omit `boardId` for every board. */
  createMcpToken: (payload: { name: string; boardId?: string }) =>
    request<{ token: string; record: McpToken }>("/mcp-tokens", { method: "POST", ...body(payload) }),
  revokeMcpToken: (tokenId: string) => request<McpToken>(`/mcp-tokens/${tokenId}`, { method: "DELETE" }),
  updateMember: (userId: string, role: InvitableRole) =>
    request<User>(`/auth/members/${userId}`, { method: "PATCH", ...body({ role }) }),
  removeMember: (userId: string) => request<User>(`/auth/members/${userId}`, { method: "DELETE" }),

  /* ---- projects ----
   * Directories on the machine running the API, not on the machine running this
   * browser: the path is validated there, which is why an invalid one comes back
   * as a 400 rather than being caught in the form.
   */
  listProjects: () => request<{ projects: Project[] }>("/projects"),
  /** Subfolders of a mounted project root; the roots themselves when `path` is omitted. */
  browseProjects: (path?: string) =>
    request<ProjectBrowse>(`/projects/browse${path ? `?path=${encodeURIComponent(path)}` : ""}`),
  /** A new empty folder inside a mounted root; picking it registers it. */
  createProjectDirectory: (parent: string, name: string) =>
    request<ProjectDirectory>("/projects/directories", { method: "POST", ...body({ parent, name }) }),
  createProject: (payload: ProjectPayload) => request<Project>("/projects", { method: "POST", ...body(payload) }),
  updateProject: (projectId: string, patch: Partial<ProjectPayload>) =>
    request<Project>(`/projects/${projectId}`, { method: "PATCH", ...body(patch) }),
  /** The boards and cards a delete would take with it. Read before offering the button. */
  projectOverviews: () => request<{ overviews: ProjectOverview[] }>("/projects/overview"),
  projectUsage: (projectId: string) => request<ProjectUsage>(`/projects/${projectId}/usage`),
  /** A project's env files: key names, size, mode, git state. Never values (admin only). */
  projectEnv: (projectId: string) => request<ProjectEnvStatus>(`/projects/${projectId}/env`),
  /** Writes one env file. The response names keys only; the content is never sent back. */
  writeProjectEnv: (projectId: string, filename: string, payload: { content: string; mode: "replace" | "merge" }) =>
    request<WriteProjectEnvResult>(`/projects/${projectId}/env/${encodeURIComponent(filename)}`, { method: "PUT", ...body(payload) }),
  /** One env file's text — the only read that returns values. Recorded in activity every time. */
  revealProjectEnv: (projectId: string, filename: string) =>
    request<{ filename: string; content: string; keys: string[] }>(
      `/projects/${projectId}/env/${encodeURIComponent(filename)}/reveal`,
      { method: "POST" },
    ),
  deleteProjectEnv: (projectId: string, filename: string) =>
    request<{ deleted: string }>(`/projects/${projectId}/env/${encodeURIComponent(filename)}`, { method: "DELETE" }),
  /**
   * Deletes the project *and* the boards and cards that pointed at it. Nothing on
   * disk is touched. Without `confirmCascade` the API refuses with a 409 whenever
   * anything points at it, so the checkbox is not the only guard.
   */
  deleteProject: (projectId: string, options: { confirmCascade?: boolean } = {}) =>
    request<{ id: string; deletedBoards: number; deletedTasks: number }>(
      `/projects/${projectId}?confirmCascade=${options.confirmCascade === true}`,
      { method: "DELETE" },
    ),

  /* ---- agents ----
   * Posting a message only queues it (202). What the agent does next arrives over
   * the websocket as `agent.message` events, not in the response.
   */
  listAgents: () => request<{ agents: AgentWithContext[] }>("/agents"),
  getAgent: (agentId: string) => request<AgentWithContext>(`/agents/${agentId}`),
  createAgent: (payload: AgentPayload) => request<AgentWithContext>("/agents", { method: "POST", ...body(payload) }),
  updateAgent: (agentId: string, patch: Partial<AgentPayload>) =>
    request<AgentWithContext>(`/agents/${agentId}`, { method: "PATCH", ...body(patch) }),
  deleteAgent: (agentId: string) => request<{ id: string }>(`/agents/${agentId}`, { method: "DELETE" }),
  /* A foreign agent: Claude Code on someone's own machine, linked by `board-link`. */
  createForeignAgent: (payload: ForeignAgentPayload) =>
    request<{ agent: AgentWithContext } & AgentPairingCode>("/agents/foreign", { method: "POST", ...body(payload) }),
  /** A fresh pairing code: to re-pair, or to move it to another machine. */
  newPairingCode: (agentId: string) => request<AgentPairingCode>(`/agents/${agentId}/link/code`, { method: "POST" }),
  /** Kills the live connection and the token; the agent stays, unpaired. */
  revokeAgentLink: (agentId: string) => request<void>(`/agents/${agentId}/link`, { method: "DELETE" }),
  /** Confine its board calls to one board; null is every board. */
  setAgentLinkBoard: (agentId: string, boardId: string | null) =>
    request<void>(`/agents/${agentId}/link`, { method: "PATCH", ...body({ boardId }) }),
  listMcpServers: () => request<{ servers: McpServer[] }>("/mcp-servers"),
  createMcpServer: (payload: McpServerPayload) => request<McpServer>("/mcp-servers", { method: "POST", ...body(payload) }),
  updateMcpServer: (serverId: string, payload: McpServerPayload) =>
    request<McpServer>(`/mcp-servers/${serverId}`, { method: "PATCH", ...body(payload) }),
  deleteMcpServer: (serverId: string) => request<{ id: string; detachedFrom: number }>(`/mcp-servers/${serverId}`, { method: "DELETE" }),
  listRemoteMachines: () => request<RemoteMachinesView>("/remote-machines"),
  createRemoteMachine: (payload: RemoteMachinePayload) => request<RemoteMachine>("/remote-machines", { method: "POST", ...body(payload) }),
  updateRemoteMachine: (machineId: string, payload: Partial<RemoteMachinePayload>) =>
    request<RemoteMachine>(`/remote-machines/${machineId}`, { method: "PATCH", ...body(payload) }),
  deleteRemoteMachine: (machineId: string) => request<{ id: string; name: string }>(`/remote-machines/${machineId}`, { method: "DELETE" }),
  listAgentFolders: () => request<{ folders: AgentFolder[] }>("/agent-folders"),
  /** A folder of these agents; no name proposes one from their projects. */
  createAgentFolder: (payload: { name?: string; agentIds: string[] }) =>
    request<AgentFolder>("/agent-folders", { method: "POST", ...body(payload) }),
  renameAgentFolder: (folderId: string, name: string) =>
    request<AgentFolder>(`/agent-folders/${folderId}`, { method: "PATCH", ...body({ name }) }),
  /** Removes the folder; its agents go back to the list, loose. */
  ungroupAgentFolder: (folderId: string) =>
    request<{ folderId: string; agentIds: string[] }>(`/agent-folders/${folderId}`, { method: "DELETE" }),
  /** Saved window layouts: one list for the installation, shown in the Windows menu. */
  listLayouts: () => request<{ layouts: WorkspaceLayout[] }>("/layouts"),
  createLayout: (payload: { name: string; layout: SavedLayoutNode; nav?: SavedLayoutNav | null }) =>
    request<WorkspaceLayout>("/layouts", { method: "POST", ...body(payload) }),
  updateLayout: (layoutId: string, payload: { name?: string; layout?: SavedLayoutNode; nav?: SavedLayoutNav | null }) =>
    request<WorkspaceLayout>(`/layouts/${layoutId}`, { method: "PATCH", ...body(payload) }),
  deleteLayout: (layoutId: string) => request<{ id: string; name: string }>(`/layouts/${layoutId}`, { method: "DELETE" }),
  /** Agents' dev-server previews (admins): every open one, or one agent's. */
  listPreviews: () => request<{ previews: Preview[] }>("/previews"),
  /** A new expiry, `ttl` minutes from now (5–1440). */
  extendPreview: (previewId: string, ttl: number) => request<Preview>(`/previews/${previewId}`, { method: "PATCH", ...body({ ttl }) }),
  closePreview: (previewId: string) => request<Preview>(`/previews/${previewId}/close`, { method: "POST" }),
  /** Pause: nothing runs (a running turn is killed) until resumed; messages queue. */
  pauseAgent: (agentId: string) => request<AgentWithContext>(`/agents/${agentId}/stop`, { method: "POST" }),
  /** Stop: kill the running turn now; the agent stays live. `clearQueue` drops what waits behind it. */
  stopAgentTurn: (agentId: string, clearQueue = false) =>
    request<{ stoppedTurnId: string | null; cleared: number }>(`/agents/${agentId}/stop-turn`, { method: "POST", ...body({ clearQueue }) }),
  /** Stop one run by id — a parallel run, beside the agent's main session. */
  stopAgentRun: (agentId: string, turnId: string) =>
    request<{ stoppedTurnId: string | null; cleared: number }>(`/agents/${agentId}/stop-turn`, { method: "POST", ...body({ turnId }) }),
  resumeAgent: (agentId: string) => request<AgentWithContext>(`/agents/${agentId}/resume`, { method: "POST" }),
  agentTriggers: (agentId: string) => request<{ triggers: AgentTrigger[] }>(`/agents/${agentId}/triggers`),
  /** A webhook's `token` is in this response only. */
  createAgentTrigger: (
    agentId: string,
    payload: { name: string; kind: "webhook" | "cron"; instruction: string; schedule?: string; timezone?: string },
  ) => request<{ trigger: AgentTrigger; token: string | null }>(`/agents/${agentId}/triggers`, { method: "POST", ...body(payload) }),
  updateAgentTrigger: (
    triggerId: string,
    patch: { name?: string; instruction?: string; schedule?: string; timezone?: string; enabled?: boolean; rotateToken?: boolean },
  ) => request<{ trigger: AgentTrigger; token: string | null }>(`/agents/triggers/${triggerId}`, { method: "PATCH", ...body(patch) }),
  deleteAgentTrigger: (triggerId: string) => request<{ id: string }>(`/agents/triggers/${triggerId}`, { method: "DELETE" }),
  previewSchedule: (schedule: string, timezone: string, count = 3) =>
    request<{ runs: string[] }>(
      `/agents/triggers/preview?schedule=${encodeURIComponent(schedule)}&timezone=${encodeURIComponent(timezone)}&count=${count}`,
    ),
  compactAgent: (agentId: string) => request<AgentTurn>(`/agents/${agentId}/compact`, { method: "POST" }),
  resetAgent: (agentId: string) => request<AgentWithContext>(`/agents/${agentId}/reset`, { method: "POST" }),
  agentMessages: (agentId: string, options: { before?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (options.before) query.set("before", options.before);
    if (options.limit) query.set("limit", String(options.limit));
    return request<{ messages: AgentMessage[] }>(`/agents/${agentId}/messages?${query}`);
  },
  postAgentMessage: (
    agentId: string,
    payload: {
      body: string;
      attachments?: Array<{ filename: string; mime?: string; data: string }>;
      /** `uploadId`s from `uploadAgentFile`, sent ahead of the message. */
      uploads?: string[];
      /** false: not an answer (the "Answering" chip was cleared). Otherwise it answers the newest open question. */
      answers?: boolean;
    },
  ) =>
    request<{
      message: AgentMessage;
      turn: AgentTurn;
      rejected: Array<{ filename: string; reason: string }>;
      /** Other agents the message @-tagged; each got a copy in its own chat. */
      forwardedTo: Array<{ agentId: string; handle: string; turnId: string }>;
      /** It joined the run under way, read at its next step, instead of queueing. */
      live: boolean;
    }>(
      `/agents/${agentId}/messages`,
      { method: "POST", ...body(payload) },
    ),
  /** Answer a question, or accept / dismiss a suggestion. Answer and accept wake the agent. */
  respondToAgent: (
    agentId: string,
    messageId: string,
    payload: {
      action: "answer" | "accept" | "dismiss";
      body?: string;
      attachments?: Array<{ filename: string; mime?: string; data: string }>;
      uploads?: string[];
    },
  ) =>
    request<{ ask: AgentMessage; reply: { message: AgentMessage; rejected: Array<{ filename: string; reason: string }> } | null }>(
      `/agents/${agentId}/messages/${messageId}/respond`,
      { method: "POST", ...body(payload) },
    ),
  /** Save an edit of an email draft an agent posted; the old version stays in its revisions. */
  saveEmailDraft: (
    agentId: string,
    messageId: string,
    fields: { to: string[]; cc: string[]; subject: string; body: string; inReplyTo?: string | null },
  ) => request<AgentMessage>(`/agents/${agentId}/messages/${messageId}/email`, { method: "PUT", ...body(fields) }),
  uploadAgentFile,
  agentAttachmentUrl: (attachmentId: string) => `/api/agents/attachments/${attachmentId}/content`,
  /** The chat's Download: for a file posted to be deleted after download, this is its one download. */
  agentAttachmentDownloadUrl: (attachmentId: string) => `/api/agents/attachments/${attachmentId}/content?download=1`,
  /** Delete a file in an agent's chat: its bytes go and its link 404s. */
  deleteAgentAttachment: (attachmentId: string) =>
    request<{ attachment: AgentAttachment }>(`/agents/attachments/${attachmentId}`, { method: "DELETE" }),
  /** Make public lets the link work without a session; Make private replaces the link, so the old one is dead. */
  setAgentAttachmentPublic: (attachmentId: string, makePublic: boolean) =>
    request<{ attachment: AgentAttachment }>(`/agents/attachments/${attachmentId}/public`, { method: makePublic ? "POST" : "DELETE" }),
  agentTurns: (agentId: string, status?: AgentTurnStatus[]) =>
    request<{ turns: AgentTurn[] }>(`/agents/${agentId}/turns${status ? `?status=${status.join(",")}` : ""}`),
  cancelAgentTurn: (turnId: string) => request<AgentTurn>(`/agents/turns/${turnId}`, { method: "DELETE" }),

  listBoards: (includeArchived = false) =>
    request<{ boards: BoardDetail[] }>(`/boards?includeArchived=${includeArchived}`),
  getBoard: (boardId: string) => request<BoardDetail>(`/boards/${boardId}`),
  createBoard: (payload: CreateBoardPayload) => request<BoardDetail>("/boards", { method: "POST", ...body(payload) }),
  updateBoard: (boardId: string, patch: Partial<CreateBoardPayload> & { archived?: boolean }) =>
    request<BoardDetail>(`/boards/${boardId}`, { method: "PATCH", ...body(patch) }),
  deleteBoard: (boardId: string) =>
    request<{ id: string; deletedTasks: number }>(`/boards/${boardId}`, { method: "DELETE" }),
  boardActivity: (boardId: string, limit = 40) =>
    request<{ activity: ActivityEntry[] }>(`/boards/${boardId}/activity?limit=${limit}`),

  /**
   * URL of the board as a spreadsheet. Returned rather than fetched: the browser
   * has to navigate to it for the download to land as a file, and putting it
   * through `fetch` would only buffer bytes we then have to hand back anyway.
   */
  exportUrl: (boardId: string, format: "csv" | "xlsx") => `/api/boards/${boardId}/export?format=${format}`,

  addColumn: (boardId: string, payload: { name: string; kind?: ColumnKind; position?: number; wipLimit?: number | null }) =>
    request<BoardColumn>(`/boards/${boardId}/columns`, { method: "POST", ...body(payload) }),
  updateColumn: (
    boardId: string,
    columnId: string,
    patch: { name?: string; kind?: ColumnKind; position?: number; wipLimit?: number | null },
  ) => request<BoardColumn>(`/boards/${boardId}/columns/${columnId}`, { method: "PATCH", ...body(patch) }),
  deleteColumn: (boardId: string, columnId: string, moveTasksTo?: string) =>
    request<{ movedTasks: number; movedTo: string | null }>(
      `/boards/${boardId}/columns/${columnId}${moveTasksTo ? `?moveTasksTo=${encodeURIComponent(moveTasksTo)}` : ""}`,
      { method: "DELETE" },
    ),

  /* ---- canvas boards ---- */
  canvas: (boardId: string) => request<Canvas>(`/boards/${boardId}/canvas`),
  /** Hung under `parentNodeId` (the root when left out). A card may be on a canvas once (409 names `existingNodeId`). */
  addCanvasNode: (boardId: string, payload: CanvasNodePayload) =>
    request<CanvasNode>(`/boards/${boardId}/canvas/nodes`, { method: "POST", ...body(payload) }),
  /** `parentNodeId` moves it (a loop is refused); `imageUrl: null` removes any image. */
  updateCanvasNode: (nodeId: string, patch: Omit<CanvasNodePayload, "kind"> & { parentNodeId?: string }) =>
    request<CanvasNode>(`/canvas/nodes/${nodeId}`, { method: "PATCH", ...body(patch) }),
  /** Its children re-hang on its parent; a linked card is never deleted. */
  deleteCanvasNode: (nodeId: string) => request<{ id: string; reattached: number }>(`/canvas/nodes/${nodeId}`, { method: "DELETE" }),
  addCanvasEdge: (boardId: string, payload: { fromNodeId: string; toNodeId: string; kind?: CanvasEdgeKind; label?: string }) =>
    request<CanvasEdge>(`/boards/${boardId}/canvas/edges`, { method: "POST", ...body(payload) }),
  deleteCanvasEdge: (edgeId: string) => request<unknown>(`/canvas/edges/${edgeId}`, { method: "DELETE" }),
  /** Where dragged nodes now sit, batched. */
  moveCanvasNodes: (boardId: string, positions: Array<{ id: string; x: number; y: number }>) =>
    request<unknown>(`/boards/${boardId}/canvas/positions`, { method: "POST", ...body({ positions }) }),
  /** Clears every saved position, so the layout is computed afresh. */
  arrangeCanvas: (boardId: string) => request<unknown>(`/boards/${boardId}/canvas/arrange`, { method: "POST" }),
  collapseCanvas: (boardId: string, collapsed: boolean) =>
    request<unknown>(`/boards/${boardId}/canvas/collapse`, { method: "POST", ...body({ collapsed }) }),
  /** The image as the raw body; PNG, JPEG, GIF or WebP up to 10MB. */
  uploadCanvasNodeImage: (nodeId: string, file: File) =>
    request<CanvasNode>(`/canvas/nodes/${nodeId}/image?filename=${encodeURIComponent(file.name || "image")}`, {
      method: "POST",
      headers: { "content-type": file.type || "application/octet-stream" },
      body: file,
    }),

  createTask: (boardId: string, payload: CreateTaskPayload) =>
    request<Task>(`/boards/${boardId}/tasks`, { method: "POST", ...body(payload) }),
  getTask: (taskId: string) => request<TaskDetail>(`/tasks/${taskId}`),
  /** `waitingOn: null` clears a hand-set "waiting on you" flag — "Mark answered". */
  updateTask: (taskId: string, patch: Partial<CreateTaskPayload> & { blockedReason?: string | null; waitingOn?: null }) =>
    request<Task>(`/tasks/${taskId}`, { method: "PATCH", ...body(patch) }),
  moveTask: (taskId: string, payload: { column: string; index?: number; blockedReason?: string | null; force?: boolean }) =>
    request<Task>(`/tasks/${taskId}/move`, { method: "POST", ...body(payload) }),
  deleteTask: (taskId: string) => request<{ id: string }>(`/tasks/${taskId}`, { method: "DELETE" }),

  listTasks: (params: Record<string, string | boolean | number | undefined>) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") query.set(key, String(value));
    }
    return request<{ tasks: TaskWithContext[] }>(`/tasks?${query.toString()}`);
  },

  comments: (taskId: string) => request<{ comments: TaskComment[] }>(`/tasks/${taskId}/comments`),
  /**
   * The response carries any `@claude` the comment raised, so the UI can confirm
   * the request registered instead of leaving the user to infer it from a
   * highlighted word.
   */
  addComment: (taskId: string, text: string, images: Array<{ filename: string; mime?: string; data: string }> = []) =>
    request<TaskComment & { mentions: Mention[] }>(`/tasks/${taskId}/comments`, {
      method: "POST",
      ...body({ body: text, images }),
    }),
  /** Direct URL, for an `<img>` — not fetched through `request`. */
  commentImageUrl: (attachmentId: string) => `/api/tasks/attachments/${attachmentId}/content`,
  /* ---- the board's intake chat ----
   * Posting only queues: this process has no model access, so `202` with a pending
   * message is the honest answer and the cards arrive on the revision poll.
   * Attachments travel as base64 inside the JSON, which keeps the server free of a
   * multipart dependency for a handful of files.
   */
  intake: (boardId: string, limit = 100) =>
    request<{ messages: IntakeMessageWithFiles[]; summary: BoardIntakeSummary }>(
      `/boards/${boardId}/intake?limit=${limit}`,
    ),
  postIntake: (
    boardId: string,
    payload: {
      instruction?: string;
      content?: string | null;
      attachments?: Array<{ filename: string; mime?: string; data: string }>;
    },
  ) =>
    request<{ message: IntakeMessageWithFiles; rejected: IntakeRejection[] }>(`/boards/${boardId}/intake`, {
      method: "POST",
      ...body(payload),
    }),
  /** Drops whatever is queued, so a dead run cannot wedge the composer. */
  cancelIntake: (boardId: string, reason?: string) =>
    request<IntakeMessageWithFiles>(`/boards/${boardId}/intake/pending`, { method: "DELETE", ...body({ reason }) }),
  deleteIntakeMessage: (messageId: string) =>
    request<{ id: string }>(`/intake/messages/${messageId}`, { method: "DELETE" }),
  /** Direct URL, for an `<img>` or a link — not fetched through `request`. */
  attachmentUrl: (attachmentId: string) => `/api/intake/attachments/${attachmentId}/content`,

  /* ---- WhatsApp exports ----
   * The upload sends the zip itself as the body rather than base64 inside JSON: an
   * export with media is often tens of megabytes. Settings ride in the query string.
   * Like the intake chat it only queues — the cards arrive on the revision poll.
   */
  whatsapp: (boardId: string) =>
    request<{ chats: WhatsAppChat[]; imports: WhatsAppImport[]; summary: BoardWhatsAppSummary }>(
      `/boards/${boardId}/whatsapp`,
    ),
  uploadWhatsApp: (
    boardId: string,
    file: File,
    options: { instruction?: string; readPhotos?: boolean; chat?: string; selfName?: string; since?: string },
  ) => {
    const query = new URLSearchParams({ filename: file.name });
    for (const [key, value] of Object.entries(options)) {
      if (value === undefined || value === "" || value === false) continue;
      query.set(key, String(value));
    }
    return request<{ import: WhatsAppImport; chat: WhatsAppChat; notes: string[] }>(
      `/boards/${boardId}/whatsapp?${query}`,
      // Only a zip type is accepted: `text/plain` (what a `.txt` export reports) is one a
      // cross-site form may send without a preflight, so the server refuses it.
      {
        method: "POST",
        body: file,
        headers: {
          "content-type": /^application\/(zip|x-zip-compressed)$/.test(file.type) ? file.type : "application/octet-stream",
        },
      },
    );
  },
  boardMcp: (boardId: string) => request<BoardMcpConfig>(`/boards/${boardId}/mcp`),
  taskPhotos: (taskId: string) => request<{ photos: TaskPhoto[] }>(`/tasks/${taskId}/photos`),
  /** Direct URL, for an `<img>` — not fetched through `request`. */
  whatsappMediaUrl: (mediaId: string) => `/api/whatsapp/media/${mediaId}/content`,
  cancelWhatsAppImport: (importId: string) =>
    request<WhatsAppImport>(`/whatsapp/imports/${importId}/cancel`, { method: "POST", ...body({}) }),
  deleteWhatsAppImport: (importId: string) =>
    request<{ id: string }>(`/whatsapp/imports/${importId}`, { method: "DELETE" }),
  /** Forgets the chat on this board — its watermark, imports and kept photos. Cards stay. */
  deleteWhatsAppChat: (boardId: string, chatId: string) =>
    request<{ id: string }>(`/boards/${boardId}/whatsapp/chats/${chatId}`, { method: "DELETE" }),

  /* ---- draft replies ----
   * A card imported from a mail is half a card without the message the user owes
   * back. Two of these only *queue* work: asking Claude to rewrite a draft, and
   * asking for a card's first drafts, both need a model this process cannot reach,
   * so they answer 202 and the result arrives on the revision poll. There is no
   * send endpoint on purpose — `setResponseStatus(id, "sent")` records that the
   * user sent it themselves.
   */
  taskResponses: (taskId: string) => request<TaskResponseSummary>(`/tasks/${taskId}/responses`),
  /** Queues a first drafting pass for a card. */
  requestDrafts: (taskId: string, instruction?: string) =>
    request<{ turn: ResponseTurnWithContext; alreadyQueued: boolean }>(`/tasks/${taskId}/responses/draft`, {
      method: "POST",
      ...body({ instruction }),
    }),
  cancelDrafts: (taskId: string, reason?: string) =>
    request<ResponseTurnWithContext>(`/tasks/${taskId}/responses/draft`, { method: "DELETE", ...body({ reason }) }),

  getResponse: (responseId: string) => request<ResponseWithContext>(`/responses/${responseId}`),
  /** Hand edits. Content only; status moves through its own route. */
  updateResponse: (
    responseId: string,
    patch: {
      body?: string;
      subject?: string | null;
      recipientName?: string;
      recipientRef?: string | null;
      cc?: string[];
      stage?: ResponseStage;
    },
  ) => request<TaskResponse>(`/responses/${responseId}`, { method: "PATCH", ...body(patch) }),
  setResponseStatus: (responseId: string, status: ResponseStatus) =>
    request<TaskResponse>(`/responses/${responseId}/status`, { method: "POST", ...body({ status }) }),
  /** Queues a rewrite. `alreadyQueued` means one was already in flight. */
  reviseResponse: (responseId: string, instruction: string) =>
    request<{ turn: ResponseTurnWithContext; alreadyQueued: boolean }>(`/responses/${responseId}/revise`, {
      method: "POST",
      ...body({ instruction }),
    }),
  cancelRevision: (responseId: string, reason?: string) =>
    request<ResponseTurnWithContext>(`/responses/${responseId}/turn`, { method: "DELETE", ...body({ reason }) }),
  deleteResponse: (responseId: string) => request<{ id: string }>(`/responses/${responseId}`, { method: "DELETE" }),

  /**
   * Every card waiting on the user's answer, oldest first — an agent's open
   * question, a reply that is theirs to send, a hand-set flag — plus open questions
   * asked in an agent chat about no listed card. Admin only (403
   * otherwise), because a question is what an agent said.
   */
  waiting: (boardId?: string) =>
    request<{ cards: WaitingCard[]; questions?: WaitingChatQuestion[]; reviews?: WaitingReviewCard[] }>(`/waiting${boardId ? `?boardId=${encodeURIComponent(boardId)}` : ""}`),

  mentions: (taskId: string, status?: MentionStatus) =>
    request<{ mentions: MentionWithContext[] }>(
      `/tasks/${taskId}/mentions${status ? `?status=${status}` : ""}`,
    ),
};
